import { useId } from "react";
import { SegmentedControl } from "../ui";
import { kgToLb, lbToKg } from "../../lib/units";
import "../Planner/planner.css";
import "./modals.css";

/**
 * Shared physiology field group (audit medium finding — de-dupe).
 *
 * `stacked` — compact sex / weight stacks (narrow contexts).
 * `tune-split` — 50/50 sex | weight with manual-setup uppercase labels;
 *   used inside {@link TunePhysiologyCard} for onboarding manual and Preferences.
 * `wizard-split` — same layout as `tune-split` but wizard field labels (`fgp-wz-fuel__label`).
 */

export type Sex = "male" | "female" | "non-binary" | "other";
export type WeightUnit = "kg" | "lb";

export function weightPlaceholder(unit: WeightUnit): string {
  return unit === "kg" ? "e.g. 68" : "e.g. 150";
}

export const SEX_OPTIONS: { value: Sex; label: string }[] = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "non-binary", label: "Non-binary" },
  { value: "other", label: "Prefer not to say" }
];

export function PhysiologyFields({
  sex,
  weight,
  weightUnit,
  onSexChange,
  onWeightChange,
  onWeightUnitChange,
  disabled = false,
  variant = "stacked",
  hint
}: {
  sex: Sex | null;
  /** Raw weight input string (parsed by the parent via `physiologyFromInputs`). */
  weight: string;
  weightUnit: WeightUnit;
  onSexChange: (sex: Sex | null) => void;
  onWeightChange: (weight: string) => void;
  onWeightUnitChange: (unit: WeightUnit) => void;
  disabled?: boolean;
  variant?: "stacked" | "tune-split" | "wizard-split";
  /** Wizard About-you step — helper copy sits under the labels, above the fields. */
  hint?: string;
}) {
  const sexLabelId = useId();
  const weightFieldId = useId();

  // Toggling the unit auto-converts a non-empty input value so the displayed
  // figure keeps meaning the same physical weight (e.g. 150 lb → 68 kg).
  const handleWeightUnitChange = (next: WeightUnit) => {
    if (next !== weightUnit) {
      const n = parseFloat(weight);
      if (weight.trim() !== "" && Number.isFinite(n)) {
        const converted = next === "kg" ? lbToKg(n) : kgToLb(n);
        onWeightChange(String(Math.round(converted * 10) / 10));
      }
    }
    onWeightUnitChange(next);
  };

  const chips = SEX_OPTIONS.map((o) => {
    const on = sex === o.value;
    return (
      <button
        key={o.value}
        type="button"
        className={`fgm-phys__chip${on ? " fgm-phys__chip--on" : ""}`}
        aria-pressed={on}
        disabled={disabled}
        onClick={() => onSexChange(on ? null : o.value)}>
        {o.label}
      </button>
    );
  });

  const weightBlock = (
    <>
      <label className="fgp-form__label-text" htmlFor={weightFieldId}>
        Weight
      </label>
      <div className="fgm-phys__weightrow">
        <input
          id={weightFieldId}
          type="number"
          inputMode="decimal"
          className="fg-input fgm-phys__weight-input"
          min={0}
          // F-03-5: mirror the server cap (profileRoute weight.max(500), same scale
          // in kg or lb) so the field signals its range instead of the value only
          // failing as an opaque 400 on apply. 500 in either unit is far beyond any
          // human weight, so no legitimate entry is blocked.
          max={500}
          placeholder={weightPlaceholder(weightUnit)}
          value={weight}
          disabled={disabled}
          onChange={(e) => onWeightChange(e.target.value)}
        />
        <SegmentedControl
          ariaLabel="Weight unit"
          options={[
            { value: "lb", label: "lb" },
            { value: "kg", label: "kg" }
          ]}
          value={weightUnit}
          onChange={(u) => handleWeightUnitChange(u as WeightUnit)}
        />
      </div>
    </>
  );

  if (variant === "wizard-split") {
    return (
      <div className="fg-tunePhys__split">
        <div className="fg-tunePhys__col">
          <div className="fgp-wz-fuel__label" id={sexLabelId}>
            Sex <span className="fgp-wz-fuel__label-hint">— optional</span>
          </div>
          {hint ? <p className="fgp-wz-fuel__desc">{hint}</p> : null}
          <div className="fgm-phys__sexrow fg-tunePhys__sex" role="group" aria-labelledby={sexLabelId}>
            {chips}
          </div>
        </div>
        <div className="fg-tunePhys__col">
          <label className="fgp-wz-fuel__label" htmlFor={weightFieldId}>
            Weight <span className="fgp-wz-fuel__label-hint">— optional</span>
          </label>
          <div className="fgm-phys__weightrow fg-tunePhys__weight">
            <input
              id={weightFieldId}
              type="number"
              inputMode="decimal"
              className="fg-input fgm-phys__weight-input"
              min={0}
              placeholder={weightPlaceholder(weightUnit)}
              value={weight}
              disabled={disabled}
              onChange={(e) => onWeightChange(e.target.value)}
            />
            <SegmentedControl
              ariaLabel="Weight unit"
              options={[
                { value: "lb", label: "lb" },
                { value: "kg", label: "kg" }
              ]}
              value={weightUnit}
              onChange={(u) => handleWeightUnitChange(u as WeightUnit)}
            />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "tune-split") {
    const labelClass = "fg-tunePhys__manual-label";
    const optClass = "fg-tunePhys__manual-opt";
    const optText = "optional";
    return (
      <div className="fg-tunePhys__split">
        <div className="fg-tunePhys__col">
          <div className={labelClass} id={sexLabelId}>
            Sex <span className={optClass}>{optText}</span>
          </div>
          <div className="fgm-phys__sexrow fg-tunePhys__sex" role="group" aria-labelledby={sexLabelId}>
            {chips}
          </div>
        </div>
        <div className="fg-tunePhys__col">
          <label className={labelClass} htmlFor={weightFieldId}>
            Weight <span className={optClass}>{optText}</span>
          </label>
          <div className="fgm-phys__weightrow fg-tunePhys__weight">
            <input
              id={weightFieldId}
              type="number"
              inputMode="decimal"
              className="fg-input fgm-phys__weight-input"
              min={0}
              placeholder={weightPlaceholder(weightUnit)}
              value={weight}
              disabled={disabled}
              onChange={(e) => onWeightChange(e.target.value)}
            />
            <SegmentedControl
              ariaLabel="Weight unit"
              options={[
                { value: "lb", label: "lb" },
                { value: "kg", label: "kg" }
              ]}
              value={weightUnit}
              onChange={(u) => handleWeightUnitChange(u as WeightUnit)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fgm-phys">
      <div className="fgp-col-gap8">
        <span className="fgp-form__label-text" id={sexLabelId}>
          Sex
        </span>
        <div className="fgm-phys__sexrow" role="group" aria-labelledby={sexLabelId}>
          {chips}
        </div>
      </div>
      <div className="fgp-col-gap8">{weightBlock}</div>
    </div>
  );
}
